import { useState, useRef, useEffect } from "react";
import { X, Plus } from "lucide-react";
import { getTradeTagsByUser, createTradeTag, useQuery, useAction } from "wasp/client/operations";
import { Badge } from "../client/components/ui/badge";
import { Input } from "../client/components/ui/input";
import { toast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";
import { findExistingTag, normalizeTagName, MAX_TAG_NAME_LENGTH } from "./tradeTagUtils";

interface TradeTagOption {
  id: string;
  name: string;
  tradeCount?: number;
}

interface TradeTagSelectorProps {
  selectedTagIds: string[];
  onChange: (tagIds: string[]) => void;
  disabled?: boolean;
}

/**
 * Sélecteur de tags d'un trade, avec autocomplétion et création à la volée.
 * Distinct de TagInput, qui reste dédié aux custom fields (string[] libres).
 */
export function TradeTagSelector({ selectedTagIds, onChange, disabled }: TradeTagSelectorProps) {
  const t = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: allTags = [], refetch } = useQuery(getTradeTagsByUser);
  const createTagFn = useAction(createTradeTag);

  const tags = allTags as TradeTagOption[];
  const selectedTags = tags.filter((tag) => selectedTagIds.includes(tag.id));

  const trimmed = normalizeTagName(inputValue);

  // Suggestions : tags non déjà sélectionnés, filtrés par la saisie, triés par
  // usage décroissant pour remonter les tags habituels de l'utilisateur.
  const filteredSuggestions = tags
    .filter(
      (tag) =>
        !selectedTagIds.includes(tag.id) &&
        tag.name.toLowerCase().includes(trimmed.toLowerCase()),
    )
    .sort((a, b) => (b.tradeCount ?? 0) - (a.tradeCount ?? 0))
    .slice(0, 10);

  // Proposer la création seulement si la saisie ne correspond exactement à
  // aucun tag existant (casse ignorée).
  const canCreate = trimmed.length > 0 && !findExistingTag(tags, trimmed);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addTagId = (tagId: string) => {
    if (!selectedTagIds.includes(tagId)) {
      onChange([...selectedTagIds, tagId]);
    }
    setInputValue("");
    setShowSuggestions(false);
  };

  const removeTagId = (tagId: string) => {
    onChange(selectedTagIds.filter((id) => id !== tagId));
  };

  const handleCreate = async () => {
    // Le garde isCreating protège du double-submit : sans contrainte d'unicité
    // en base, deux appels concurrents créeraient deux tags homonymes.
    if (!trimmed || isCreating) return;
    setIsCreating(true);
    try {
      const created = await createTagFn({ name: trimmed });
      await refetch();
      addTagId((created as TradeTagOption).id);
    } catch (err: any) {
      // La sélection reste inchangée si la création échoue.
      toast({
        title: t.journal.tagCreateError,
        description: err?.message,
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const exact = findExistingTag(tags, trimmed);
      if (exact) {
        addTagId(exact.id);
      } else if (canCreate) {
        void handleCreate();
      }
    } else if (e.key === "Backspace" && !inputValue && selectedTagIds.length > 0) {
      removeTagId(selectedTagIds[selectedTagIds.length - 1]);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="flex flex-wrap gap-2 p-2 border rounded-md min-h-[42px] focus-within:ring-2 focus-within:ring-ring">
        {selectedTags.map((tag) => (
          <Badge key={tag.id} variant="secondary" className="px-2 py-1">
            {tag.name}
            <button
              type="button"
              onClick={() => removeTagId(tag.id)}
              disabled={disabled}
              className="ml-2 hover:text-destructive"
              aria-label={`${tag.name} — ${t.common.delete}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          maxLength={MAX_TAG_NAME_LENGTH}
          disabled={disabled}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowSuggestions(true);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          placeholder={selectedTags.length === 0 ? t.journal.tagsPlaceholder : ""}
          className="flex-1 min-w-[120px] border-0 focus-visible:ring-0 focus-visible:ring-offset-0 p-0 h-auto"
        />
      </div>
      {showSuggestions && (filteredSuggestions.length > 0 || canCreate) && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredSuggestions.map((tag) => (
            <button
              key={tag.id}
              type="button"
              onClick={() => addTagId(tag.id)}
              className="w-full text-left px-3 py-2 hover:bg-accent hover:text-accent-foreground text-sm"
            >
              {tag.name}
            </button>
          ))}
          {canCreate && (
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={isCreating}
              className="w-full text-left px-3 py-2 hover:bg-accent hover:text-accent-foreground text-sm flex items-center gap-2 text-primary"
            >
              <Plus className="h-3.5 w-3.5" />
              {t.journal.createTag} « {trimmed} »
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default TradeTagSelector;
